/**
 * CFDI 4.0 Validation Rules — Pure functions, zero DOM dependency.
 *
 * Based on SAT Anexo 20, Guía de Llenado CFDI 4.0, and Matriz de Errores.
 * This module is safe to import from any context (browser, Node, web-worker).
 *
 * Usage:
 *   import * as R from './cfdi-rules.js';
 *   var errors = R.validateAll(cfdiPayload);
 */

import { REGIMENES_FISCALES, USOS_CFDI } from './catalogo-regimen-uso.js';

// ─── Static catalogs ────────────────────────────────────────────────────────
// c_RegimenFiscal y c_UsoCFDI NO se capturan a mano: salen de database/catalogs.db
// (la misma fuente de Catsatlib en el servidor) vía catalogo-regimen-uso.js, que
// genera scripts/generar-catalogo-regimen-uso.mjs. Las listas a mano divergieron
// del SAT: el 626 (RESICO) figuraba solo para personas físicas y bloqueaba el
// timbrado a las morales; 616 y 625 pasaban con RFC moral y 622 con RFC físico;
// G02 no admitía el 616 y D01-D10 sí admitían el 626.
// lib/__tests__/cfdi-rules-catalogo.test.js ata estas reglas al catálogo.

var RFC_PUBLICO  = 'XAXX010101000';
var RFC_EXTRANJERO = 'XEXX010101000';

/** Régimen que exige el SAT con RFC genérico (CFDI40159). */
var REGIMEN_RFC_GENERICO = '616';

/**
 * ¿La clave del catálogo (régimen o uso) aplica a este tipo de persona? El RFC
 * genérico tiene 13 caracteres: para el SAT es de persona física. Con un RFC
 * incompleto (tipo null) no se juzga.
 */
function aplicaAPersona(entrada, tp) {
  if (tp === 'moral') return entrada.moral;
  if (tp === 'fisica' || tp === 'generico') return entrada.fisica;
  return true;
}

/** Usos del CFDI (en el orden del catálogo) que admiten este régimen y tipo de persona. */
function usosDelRegimen(regimen, tp) {
  var lista = [];
  for (var uso in USOS_CFDI) {
    if (!Object.prototype.hasOwnProperty.call(USOS_CFDI, uso)) continue;
    var u = USOS_CFDI[uso];
    if (aplicaAPersona(u, tp) && u.regimenes.indexOf(regimen) !== -1) lista.push(uso);
  }
  return lista;
}

/** ['G02', 'S01', 'CP01'] → "G02, S01 o CP01". */
function enumerar(lista) {
  if (lista.length < 2) return lista.join('');
  return lista.slice(0, -1).join(', ') + ' o ' + lista[lista.length - 1];
}

/**
 * Uso del CFDI con el RFC de público en general (XAXX010101000), por tipo de
 * comprobante. El catálogo admite G02, S01 y CP01 con el 616, pero la Guía de
 * llenado del CFDI global del SAT (feb-2024) pide S01 en el Ingreso; G02 es de
 * la nota de crédito (Egreso) y CP01 del complemento de pago. Cualquier otro
 * tipo, o sin tipo: S01. La misma tabla vive en Cfdi_rules.php
 * ($usos_publico_por_tipo, la usa auto_fix): lib/__tests__/cfdi-rules-catalogo.test.js
 * falla si dejan de coincidir.
 */
var USOS_PUBLICO_POR_TIPO = { I: ['S01'], T: ['S01'], E: ['G02', 'S01'], P: ['CP01'] };

/**
 * Uso del CFDI con el RFC genérico extranjero (XEXX010101000), por tipo de
 * comprobante. La Guía de llenado del Anexo 20 (17-ene-2026, campo UsoCFDI, p. 20)
 * pide S01 en todo CFDI a un residente en el extranjero, y la de Comercio
 * Exterior lo repite (p. 23); la única excepción es el recibo de pago, CP01 (guía
 * de pagos, p. 17 y pregunta 6). A diferencia del público en general, la nota de
 * crédito no lleva G02. Cualquier otro tipo: S01. Es
 * Cfdi_rules::$usos_extranjero_por_tipo; la misma prueba ata las dos tablas.
 */
var USOS_EXTRANJERO_POR_TIPO = { P: ['CP01'] };

/** Descripción corta de los usos que salen en los mensajes del RFC genérico. */
var TEXTO_USO = { S01: 'Sin efectos fiscales', G02: 'Devoluciones, descuentos o bonificaciones', CP01: 'Pagos' };

/**
 * Usos que valen con un RFC genérico (XAXX o XEXX) para este tipo de
 * comprobante: la tabla de ese RFC, dentro de lo que el catálogo admite con el
 * 616 y en el orden de la tabla (el primero es el que pone auto_fix). Con un
 * RFC que no es genérico, null.
 */
export function usosDelRfcGenerico(rfc, tipoComprobante) {
  var tablas = isRfcPublico(rfc) ? USOS_PUBLICO_POR_TIPO
    : isRfcExtranjero(rfc) ? USOS_EXTRANJERO_POR_TIPO : null;
  if (!tablas) return null;
  var tipo = String(tipoComprobante == null ? '' : tipoComprobante).trim().toUpperCase();
  var tabla = tablas[tipo] || ['S01'];
  var catalogo = usosDelRegimen(REGIMEN_RFC_GENERICO, 'generico');
  return tabla.filter(function (uso) { return catalogo.indexOf(uso) !== -1; });
}

// ─── Helpers ────────────────────────────────────────────────────────────────

export function tipoPersona(rfc) {
  if (!rfc) return null;
  rfc = rfc.trim().toUpperCase();
  if (rfc === RFC_PUBLICO || rfc === RFC_EXTRANJERO) return 'generico';
  if (rfc.length === 12) return 'moral';
  if (rfc.length === 13) return 'fisica';
  return null;
}

export function isRfcGenerico(rfc) {
  if (!rfc) return false;
  rfc = rfc.trim().toUpperCase();
  return rfc === RFC_PUBLICO || rfc === RFC_EXTRANJERO;
}

export function isRfcPublico(rfc) {
  return rfc && rfc.trim().toUpperCase() === RFC_PUBLICO;
}

export function isRfcExtranjero(rfc) {
  return rfc && rfc.trim().toUpperCase() === RFC_EXTRANJERO;
}

// ─── Individual rule checks ─────────────────────────────────────────────────

/**
 * Rule 1: MetodoPago ↔ FormaPago
 *   - PPD exige FormaPago "99" (CFDI40105 en la matriz del SAT).
 *   - PUE con "99" NO tiene código en la matriz (no es una de sus
 *     validaciones): es regla de FacturaDigital, porque con PUE ya se sabe cómo
 *     se pagó. Por eso lleva un código interno, PUE_FORMAPAGO99, como NC_METODO
 *     y EXTRANJERO_RF. Antes decía CFDI40106, que en la matriz es "EL
 *     certificado no cumple con alguno de los valores permitidos".
 * Returns { valid, autoFix?, error? }
 */
export function checkMetodoPagoFormaPago(metodoPago, formaPago, tipoComprobante) {
  if (tipoComprobante === 'T' || tipoComprobante === 'P') {
    return { valid: true, strip: true };
  }
  if (metodoPago === 'PPD') {
    if (formaPago !== '99') {
      return {
        valid: false,
        autoFix: { formaPago: '99' },
        code: 'CFDI40105',
        error: 'Cuando el Método de Pago es PPD, la Forma de Pago debe ser "99 Por Definir".'
      };
    }
  }
  if (metodoPago === 'PUE') {
    if (formaPago === '99') {
      return {
        valid: false,
        code: 'PUE_FORMAPAGO99',
        error: 'No se permite Forma de Pago "99 Por Definir" con Método de Pago PUE. Selecciona la forma de pago real.'
      };
    }
  }
  return { valid: true };
}

/**
 * Rule 2: RegimenFiscalReceptor ↔ tipo de persona del receptor
 *   - RFC genérico (XAXX/XEXX) → régimen 616: CFDI40159.
 *   - Régimen del otro tipo de persona que su RFC: CFDI40158.
 * Ojo: CFDI40141 es esta misma regla pero para el régimen del EMISOR, y
 * CFDI40160 es el catálogo de UsoCFDI; ninguno de los dos aplica aquí.
 */
export function checkRegimenTipoPersona(regimen, rfc) {
  if (!regimen || !rfc) return { valid: true };
  if (isRfcGenerico(rfc)) {
    return regimen === REGIMEN_RFC_GENERICO
      ? { valid: true }
      : { valid: false, autoFix: { regimen: REGIMEN_RFC_GENERICO }, code: 'CFDI40159',
          error: 'Para RFC genérico el Régimen Fiscal debe ser 616 (Sin obligaciones fiscales).' };
  }
  var reg = REGIMENES_FISCALES[regimen];
  // Clave fuera del catálogo: aquí no se juzga, como antes (el PAC la rechaza con CFDI40157).
  if (!reg) return { valid: true };
  var tp = tipoPersona(rfc);
  if (tp === 'moral' && !reg.moral) {
    return { valid: false, code: 'CFDI40158',
      error: 'El Régimen Fiscal ' + regimen + ' es exclusivo de Persona Física y el RFC corresponde a Persona Moral.' };
  }
  if (tp === 'fisica' && !reg.fisica) {
    return { valid: false, code: 'CFDI40158',
      error: 'El Régimen Fiscal ' + regimen + ' es exclusivo de Persona Moral y el RFC corresponde a Persona Física.' };
  }
  return { valid: true };
}

/**
 * Rule 3: UsoCFDI ↔ tipo de persona y régimen del receptor
 *   - Uso que no existe en el catálogo c_UsoCFDI: CFDI40160.
 *   - Uso que no admite el tipo de persona o el régimen: CFDI40161. Con RFC
 *     genérico el régimen es el 616 (Rule 2), así que valen los usos que el
 *     catálogo admite con él: G02, S01 y CP01.
 *   - Con RFC genérico, además, el del tipo de comprobante según su tabla
 *     (usosDelRfcGenerico: público en general, S01 en la factura; extranjero,
 *     S01 salvo el pago). El PAC no lo rechaza (la matriz no lo valida), así que
 *     lleva código interno, GLOBAL_USO_S01, con mensaje propio de cada RFC.
 */
export function checkUsoCfdiRegimen(usoCfdi, regimenFiscal, rfc, tipoComprobante) {
  if (!usoCfdi || !regimenFiscal || !rfc) return { valid: true };
  if (isRfcGenerico(rfc)) {
    var delGenerico = usosDelRegimen(REGIMEN_RFC_GENERICO, 'generico');
    var delTipo = usosDelRfcGenerico(rfc, tipoComprobante);
    if (delGenerico.indexOf(usoCfdi) === -1) {
      return { valid: false, code: 'CFDI40161',
        error: 'Para RFC genérico el Uso del CFDI debe ser ' + enumerar(delTipo) + '.' };
    }
    if (delTipo.indexOf(usoCfdi) === -1) {
      var quien = isRfcPublico(rfc)
        ? 'Con RFC de público en general (XAXX010101000)'
        : 'Con RFC genérico extranjero (XEXX010101000)';
      return { valid: false, code: 'GLOBAL_USO_S01',
        error: quien + ', en este tipo de comprobante el Uso del CFDI debe ser ' +
          enumerar(delTipo.map(function (u) { return u + ' (' + (TEXTO_USO[u] || u) + ')'; })) + '.' };
    }
    return { valid: true };
  }

  var uso = USOS_CFDI[usoCfdi];
  if (!uso) {
    return { valid: false, code: 'CFDI40160',
      error: 'El Uso del CFDI "' + usoCfdi + '" no es válido en CFDI 4.0.' };
  }

  var tp = tipoPersona(rfc);
  if (tp === 'moral' && !uso.moral) {
    return { valid: false, code: 'CFDI40161',
      error: 'El Uso del CFDI "' + usoCfdi + '" es exclusivo de Persona Física.' };
  }
  if (tp === 'fisica' && !uso.fisica) {
    return { valid: false, code: 'CFDI40161',
      error: 'El Uso del CFDI "' + usoCfdi + '" es exclusivo de Persona Moral.' };
  }

  // Régimen fuera del catálogo: su error es el del régimen (CFDI40157), no el del uso.
  if (!REGIMENES_FISCALES[regimenFiscal]) return { valid: true };
  if (uso.regimenes.indexOf(regimenFiscal) === -1) {
    return { valid: false, code: 'CFDI40161',
      error: 'El Uso del CFDI "' + usoCfdi + '" no es compatible con el Régimen Fiscal ' + regimenFiscal + ' del receptor.' };
  }
  return { valid: true };
}

/**
 * Rule 4: Nota de crédito (tipo E). Ya no bloquea: el SAT acepta una nota de crédito con PPD (la matriz de errores 4.0 no
 * liga MetodoPago con el tipo E) y la Guía de llenado del Anexo 20 recomienda PUE (Apéndice 5). La nota toma el método
 * de su factura y, si lleva otro, se confirma en pantalla (cfdi/nc-pago.js, decisiones de Adrián del 20 y 26-oct-2026). Se
 * queda la firma por quien la llame; el aviso viaja como `aviso`, nunca como error.
 */
export function checkNotaCredito(tipoComprobante, metodoPago, usoCfdi) {
  if (tipoComprobante !== 'E' || metodoPago === 'PUE' || !metodoPago) return { valid: true };
  return { valid: true, aviso: 'Para notas de crédito, el SAT recomienda método de pago PUE (Anexo 20, Guía de llenado, Apéndice 5).' };
}

/**
 * Rule 5: Traslado rules (tipo T) — FormaPago/MetodoPago must be stripped
 */
export function checkTraslado(tipoComprobante) {
  if (tipoComprobante !== 'T') return { valid: true };
  return { valid: true, strip: { formaPago: true, metodoPago: true }, enforceUsoCfdi: 'S01',
           enforceMoneda: 'XXX', enforceSubTotal: '0', enforceTotal: '0' };
}

/**
 * Rule 6: Complemento de Pago (tipo P)
 */
export function checkComplementoPago(tipoComprobante) {
  if (tipoComprobante !== 'P') return { valid: true };
  return { valid: true, strip: { formaPago: true, metodoPago: true },
           enforceUsoCfdi: 'CP01', enforceMoneda: 'XXX',
           enforceSubTotal: '0', enforceTotal: '0' };
}

/**
 * Rule 7: Factura Global requirements (CFDI40130)
 */
export function checkFacturaGlobal(rfc, nombre, tipoComprobante, infoGlobal) {
  if (!isRfcPublico(rfc)) return { valid: true };
  var isPublicoEnGeneral = nombre && nombre.trim().toUpperCase() === 'PUBLICO EN GENERAL';
  if (isPublicoEnGeneral && tipoComprobante === 'I') {
    if (!infoGlobal || !infoGlobal.Periodicidad || !infoGlobal.Meses || !infoGlobal.Ano) {
      return { valid: false, code: 'CFDI40130',
        error: 'Para PUBLICO EN GENERAL con factura de Ingreso, se requiere la Información Global (Periodicidad, Meses, Año).' };
    }
  }
  return { valid: true };
}

// ─── Receptor extranjero (RFC XEXX010101000) ───────────────────────────────
// Matriz CFDI 4.0, cfdi:Receptor: CFDI40150 (ResidenciaFiscal fuera de c_Pais),
// CFDI40151 / CFDI40154 (Residencia / NumRegIdTrib con RFC nacional), CFDI40152
// (MEX), CFDI40153 (con NumRegIdTrib o Comercio Exterior, la Residencia es
// obligatoria: aquí siempre lo es con XEXX, EXTRANJERO_RF). XSD: NumRegIdTrib de
// 1 a 40 caracteres. Crear CFDI la usa vía window.CfdiRules (cfdi/extranjero.js
// muestra los campos; cfdi/receptor-extranjero.js decide qué viaja).
export var NUMREGIDTRIB_MAX = 40;

// c_Pais trae, para algunos países (hoy USA y CAN: [0-9]{9}), el patrón del
// número de registro tributario; el PAC rechaza con CFDI40156 el que no lo
// cumple ("12-3456789" para USA, medido en el E2E con PAC de pruebas). La vista
// lo publica en el atributo data-patron-taxid de cada país.
function regexPatron(patron) {
  var p = String(patron || '').trim();
  if (!p) return null;
  try { return new RegExp('^(?:' + p + ')$'); } catch (e) { return null; }
}

/** ¿El Tax ID cumple el patrón del país? Sin patrón (o ilegible), sí. */
export function cumplePatronTaxId(patron, valor) {
  var re = regexPatron(patron);
  return !re || re.test(String(valor == null ? '' : valor).trim());
}

/** El patrón en palabras: "[0-9]{9}" → "9 dígitos, sin guiones ni espacios". */
export function describirPatronTaxId(patron) {
  var p = String(patron || '').trim();
  var m = /^\[0-9\]\{(\d+)\}$/.exec(p);
  if (m) return m[1] + ' dígitos, sin guiones ni espacios';
  return p ? 'el formato del SAT ' + p : '';
}

/**
 * Con un patrón de solo dígitos, quita guiones, puntos y espacios ("12-3456789"
 * → "123456789"): es el mismo número escrito con separadores. Con otro patrón o
 * sin él, el valor tal cual (recortado).
 */
export function limpiarTaxId(patron, valor) {
  var v = String(valor == null ? '' : valor).trim();
  if (!/^\[0-9\]\{\d+(,\d+)?\}$/.test(String(patron || '').trim())) return v;
  var limpio = v.replace(/[\s.\-\/]/g, '');
  return /^[0-9]+$/.test(limpio) ? limpio : v;
}

/**
 * Validación previa al timbrado de la identidad fiscal del receptor. Mensajes en
 * español llano; `code` es el de la matriz del SAT cuando lo hay (el diálogo solo
 * enseña esos) o uno interno.
 * @returns {Array<{field:string, code:string, error:string}>}
 */
export function validarReceptorExtranjero(o) {
  o = o || {};
  var res = o.residenciaFiscal == null ? '' : String(o.residenciaFiscal).trim().toUpperCase();
  var tax = o.numRegIdTrib == null ? '' : String(o.numRegIdTrib).trim();
  var errores = [];

  if (!isRfcExtranjero(o.rfc)) {
    if (res) {
      errores.push({ field: 'ResidenciaFiscal', code: 'CFDI40151',
        error: 'El cliente tiene RFC mexicano, así que no lleva país de residencia fiscal: ese dato solo aplica con el RFC extranjero XEXX010101000.' });
    }
    if (tax) {
      errores.push({ field: 'NumRegIdTrib', code: 'CFDI40154',
        error: 'El cliente tiene RFC mexicano, así que no lleva número de registro tributario extranjero (Tax ID).' });
    }
    return errores;
  }

  if (!res) {
    errores.push({ field: 'ResidenciaFiscal', code: 'EXTRANJERO_RF',
      error: 'Elige el país de residencia fiscal del cliente extranjero (campo "País de residencia fiscal", en la Información del Cliente).' });
  } else if (!/^[A-Z]{3}$/.test(res)) {
    errores.push({ field: 'ResidenciaFiscal', code: 'CFDI40150',
      error: 'El país de residencia fiscal "' + res + '" no es una clave del catálogo de países del SAT. Elígelo de la lista.' });
  } else if (res === 'MEX') {
    errores.push({ field: 'ResidenciaFiscal', code: 'CFDI40152',
      error: 'Un cliente con RFC extranjero (XEXX010101000) no puede tener residencia fiscal en México. Elige su país real; si reside en México, factúrale con su RFC mexicano.' });
  }
  if (tax && !cumplePatronTaxId(o.patronNumRegIdTrib, tax)) {
    errores.push({ field: 'NumRegIdTrib', code: 'CFDI40156',
      error: 'El número de registro tributario (Tax ID) "' + tax + '" no tiene el formato que el SAT exige para ' +
        (res || 'ese país') + ': ' + describirPatronTaxId(o.patronNumRegIdTrib) + '.' });
  } else if (tax.length > NUMREGIDTRIB_MAX) {
    errores.push({ field: 'NumRegIdTrib', code: 'NUMREGIDTRIB_LARGO',
      error: 'El número de registro tributario (Tax ID) admite máximo ' + NUMREGIDTRIB_MAX +
        ' caracteres; tiene ' + tax.length + '.' });
  }
  return errores;
}

/**
 * Rule 8: Extranjero requirements (RFC XEXX010101000).
 * Residencia fiscal obligatoria (EXTRANJERO_RF, regla de FacturaDigital), clave
 * de c_Pais (CFDI40150), nunca MEX (CFDI40152); Tax ID de máximo 40 caracteres.
 * El detalle vive en cfdi/receptor-extranjero.js; aquí, el primer error.
 */
export function checkExtranjero(rfc, residenciaFiscal, numRegIdTrib, patronNumRegIdTrib) {
  if (!isRfcExtranjero(rfc)) return { valid: true };
  var errs = validarReceptorExtranjero({ rfc: rfc, residenciaFiscal: residenciaFiscal,
    numRegIdTrib: numRegIdTrib, patronNumRegIdTrib: patronNumRegIdTrib });
  if (errs.length) return { valid: false, code: errs[0].code, error: errs[0].error, errors: errs };
  return { valid: true };
}

// ─── Allowed values helpers (for dropdown filtering) ────────────────────────

/**
 * Returns array of formaPago codes valid for the given metodoPago.
 * Pass the full catalog array as `allCodes` = [{clave:'01', desc:'Efectivo'}, ...]
 */
export function allowedFormasPago(metodoPago) {
  if (metodoPago === 'PPD') return ['99'];
  if (metodoPago === 'PUE') return null; // all except 99
  return null;
}

/**
 * Returns true if formaPago code should be hidden for the given metodoPago.
 */
export function isFormaPagoHidden(clave, metodoPago) {
  if (metodoPago === 'PPD' && clave !== '99') return true;
  if (metodoPago === 'PUE' && clave === '99') return true;
  return false;
}

/**
 * Given a régimen fiscal code, return the valid UsoCFDI keys (catalog order).
 * Returns null if no filtering needed: no régimen, or a clave outside the
 * catalog (checkUsoCfdiRegimen doesn't judge those either). With a generic RFC
 * (XAXX público en general or XEXX extranjero) the list is that RFC's table for
 * the tipo de comprobante (usosDelRfcGenerico).
 */
export function allowedUsosCfdi(regimenFiscal, rfc, tipoComprobante) {
  if (!regimenFiscal) return null;
  if (isRfcGenerico(rfc)) return usosDelRfcGenerico(rfc, tipoComprobante);
  if (!REGIMENES_FISCALES[regimenFiscal]) return null;
  return usosDelRegimen(regimenFiscal, tipoPersona(rfc));
}

// ─── Full pre-timbrado validation ───────────────────────────────────────────

/**
 * Validate a complete CFDI payload object. Returns { valid, errors[] }.
 * Each error: { code, error, field?, autoFix? }
 */
export function validateAll(cfdi) {
  var errors = [];
  var tc = cfdi.TipoDeComprobante || '';
  var rfc = cfdi.Receptor && cfdi.Receptor.Rfc || '';
  var nombre = cfdi.Receptor && cfdi.Receptor.Nombre || '';
  var regimen = cfdi.Receptor && cfdi.Receptor.RegimenFiscalReceptor || '';
  var usoCfdi = cfdi.Receptor && cfdi.Receptor.UsoCFDI || '';
  var mp = cfdi.MetodoPago || '';
  var fp = cfdi.FormaPago || '';

  var r1 = checkMetodoPagoFormaPago(mp, fp, tc);
  if (!r1.valid) errors.push({ field: 'FormaPago', code: r1.code, error: r1.error, autoFix: r1.autoFix });

  if (cfdi.Version === '4.0') {
    var r2 = checkRegimenTipoPersona(regimen, rfc);
    if (!r2.valid) errors.push({ field: 'RegimenFiscalReceptor', code: r2.code, error: r2.error });

    var r3 = checkUsoCfdiRegimen(usoCfdi, regimen, rfc, tc);
    if (!r3.valid) errors.push({ field: 'UsoCFDI', code: r3.code, error: r3.error });

    var r7 = checkFacturaGlobal(rfc, nombre, tc, cfdi.InformacionGlobal);
    if (!r7.valid) errors.push({ field: 'InformacionGlobal', code: r7.code, error: r7.error });

    // Con RFC extranjero: residencia y Tax ID; con RFC nacional, que no viajen
    // (CFDI40151 / CFDI40154). Todos los errores, no solo el primero.
    validarReceptorExtranjero({
      rfc: rfc,
      residenciaFiscal: cfdi.Receptor && cfdi.Receptor.ResidenciaFiscal,
      numRegIdTrib: cfdi.Receptor && cfdi.Receptor.NumRegIdTrib,
      // No es atributo del CFDI: el patrón del Tax ID del país (data-patron-taxid).
      patronNumRegIdTrib: cfdi.Receptor && cfdi.Receptor.PatronNumRegIdTrib
    }).forEach(function (e) { errors.push(e); });
  }

  return { valid: errors.length === 0, errors: errors };
}
